//@flow
import React, {useState} from 'react';

import {makeStyles, MenuItem, TableCell} from '@material-ui/core';
import PillSelect from '../../../../../elements/PillSelect';
import {colors} from '../../../../../assets/styles/colors';
import {useProductContext} from '../../../context';

import {useEditProduct} from '../../../utils/editProduct';

import type {BulkEditProductSubCategory} from '../../../../../entities';
import {useCategories} from '../../../../categories/utils';

const useStyles = makeStyles(theme => ({
  selectField: {
    whiteSpace: 'nowrap',
    padding: '15px',
    borderRadius: '8px',
    height: '48px',
    color: colors.grey800,
    border: `1px solid ${colors.grey300}`,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectLabel: {
    whiteSpace: 'nowrap',
    maxWidth: '18ch',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  filterItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '250px',
    borderBottom: `1px solid ${colors.grey200}`,
    padding: '0.75rem 1.5rem',

    '&:last-child': {
      borderBottom: 'none',
    },

    '&[data-disabled="true"]': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  },
  filterLabel: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: '.9rem',
  },
  customCheckbox: {
    borderRadius: '50%',
    width: '1.5rem',
    height: '1.5rem',
    border: `1.25px solid ${colors.grey400}`,
    background: 'white',

    '&[data-checked="true"]': {
      border: `6px solid ${colors.primary}`,
    },
  },
  categoryName: {
    color: colors.purple,
    fontWeight: 600,
    fontSize: '1.125rem',
    maxWidth: '18ch',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    padding: '0.75rem 1rem',
  },
  subCategoryName: {
    color: colors.grey700,
    fontWeight: 500,
    fontSize: '1rem',
    maxWidth: '18ch',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    padding: '0.5rem 1rem',
    fontStyle: 'italic',
  },
}));

export default function SubCategoryItemCell() {
  const classes = useStyles();
  const [product] = useProductContext(product => ({
    id: product.id,
    subCategoryItems: product.subCategoryItems || [],
    subCategories: product.subCategories || [],
    categories: product.categories || [],
  }));

  const categories = useCategories();

  const {editProduct} = useEditProduct(product ? product.id : '');
  const categoriesSelected = product.categories;
  const subCategoriesSelected = product.subCategories;

  const [subCategoryItemsSelected, setSubCategoryItemsSelected] = useState<
    BulkEditProductSubCategory[]
  >(product && product.subCategoryItems ? product.subCategoryItems : []);

  if (!product) return null;

  // Get categories that have subcategories that are selected and have subCategoryItems
  const categoriesWithSubCategoryItems = categories.filter(category => {
    return (
      categoriesSelected.some(c => c.id === category.id) &&
      category.subCategories &&
      category.subCategories.length > 0 &&
      category.subCategories.some(
        subCategory =>
          subCategoriesSelected.some(sc => sc.id === subCategory.id) &&
          subCategory.subCategoriesItems &&
          subCategory.subCategoriesItems.length > 0
      )
    );
  });

  const disabled = categoriesWithSubCategoryItems.length === 0;

  const label = subCategoryItemsSelected
    .map(subCategoryItem => subCategoryItem.name)
    .join(', ');

  const toggleSubCategoryItem = (
    subCategoryItem: BulkEditProductSubCategory
  ) => {
    const selected = subCategoryItemsSelected.some(
      selectedSubCategoryItem =>
        selectedSubCategoryItem.id === subCategoryItem.id
    );

    if (selected) {
      setSubCategoryItemsSelected(
        subCategoryItemsSelected.filter(
          selectedSubCategoryItem =>
            selectedSubCategoryItem.id !== subCategoryItem.id
        )
      );
      return;
    }
    setSubCategoryItemsSelected([...subCategoryItemsSelected, subCategoryItem]);
  };

  const saveSubCategoryItems = () => {
    editProduct({
      subCategoryItems: subCategoryItemsSelected.map(
        subCategoryItem => subCategoryItem.id
      ),
    });
  };

  return (
    <TableCell align="right">
      <PillSelect
        label={label || 'Items de Subcategoría'}
        variant="outlined"
        disabled={disabled}
        onClose={saveSubCategoryItems}
        classes={{
          button: classes.selectField,
          label: classes.selectLabel,
        }}
      >
        {categoriesWithSubCategoryItems.map(category => {
          const selectedSubCategoriesInCategory =
            category.subCategories?.filter(subCategory =>
              subCategoriesSelected.some(sc => sc.id === subCategory.id)
            ) || [];

          const hasSelectedASubCategoryItem = selectedSubCategoriesInCategory.some(
            subCategory =>
              subCategory.subCategoriesItems?.some(subCategoryItem =>
                subCategoryItemsSelected.some(
                  selectedSubCategoryItem =>
                    selectedSubCategoryItem.id === subCategoryItem.id
                )
              )
          );

          return (
            <div key={category.id}>
              <header className={classes.categoryName}>{category.name}</header>
              {selectedSubCategoriesInCategory.map(subCategory => (
                <div key={subCategory.id}>
                  <header className={classes.subCategoryName}>
                    {subCategory.name}
                  </header>
                  {subCategory.subCategoriesItems &&
                    subCategory.subCategoriesItems.map(subCategoryItem => {
                      const selected = subCategoryItemsSelected.some(
                        selectedSubCategoryItem =>
                          selectedSubCategoryItem.id === subCategoryItem.id
                      );

                      return (
                        <MenuItem
                          key={subCategoryItem.id}
                          className={classes.filterItem}
                          onClick={() => toggleSubCategoryItem(subCategoryItem)}
                          data-disabled={
                            hasSelectedASubCategoryItem && !selected
                          }
                        >
                          <span className={classes.filterLabel}>
                            {subCategoryItem.name}
                          </span>
                          <span
                            className={classes.customCheckbox}
                            data-checked={selected}
                          />
                        </MenuItem>
                      );
                    })}
                </div>
              ))}
            </div>
          );
        })}
      </PillSelect>
    </TableCell>
  );
}
